import { authFetch, clearToken } from './services/api.js';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChefHat, Heart, Users, DollarSign, Calendar, Sparkles, ArrowRight, Star, Send, Clock, User, Plus, X, Camera } from 'lucide-react';
import './App.css';
import Chatbot from './Chatbot.jsx';
import UserRegistration from './UserRegistration.jsx';
import Logo360 from './assets/Logo360.png';
import UserDropdown from './UserDropdown.jsx';
import UserDashboard from './UserDashboard.jsx';
import UserMealPlans from './UserMealPlans.jsx';
import CycleDashboard from './CycleDashboard.jsx';
import CyclePhaseBadge from './CyclePhaseBadge.jsx';
import CycleLogger from './CycleLogger.jsx';

// API Base URL - now everything goes to port 3001
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

function App() {
  const [selectedMood, setSelectedMood] = useState('energetic');
  const [mealPlan, setMealPlan] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [newReview, setNewReview] = useState({ name: '', content: '', rating: 5 ,photo: null});
  const [isLoading, setIsLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null); 
  const [showRegistration, setShowRegistration] = useState({
    show: false,
    activePanel: 'email',
    userData360: null,
    email: ''
  });
  const [userEmail, setUserEmail] = useState('');
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [selectedMeal, setSelectedMeal] = useState(null);
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [dietaryPref, setDietaryPref] = useState('Any');
  const [cuisinePref, setCuisinePref] = useState('Any');
  const [availableIngredients, setAvailableIngredients] = useState([]);
  const [newIngredient, setNewIngredient] = useState('');
  const [isDetectingIngredients, setIsDetectingIngredients] = useState(false);
  const [userAllergies, setUserAllergies] = useState([]);
  const [newAllergy, setNewAllergy] = useState('');
  const chatRef = useRef();
  const fileInputRef = useRef();

  const [savedMealPlans, setSavedMealPlans] = useState([]);
  const [showSavedPlans, setShowSavedPlans] = useState(false);
  const [isSavingPlan, setIsSavingPlan] = useState(false);
  const [mealPlanName, setMealPlanName] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);

  const [showUserDashboard, setShowUserDashboard] = useState(false);
  const [showUserMealPlans, setShowUserMealPlans] = useState(false);

  const [showCycleDashboard, setShowCycleDashboard] = useState(false);
  const [cycleData, setCycleData] = useState(null);

  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [reviewPhotoPreview, setReviewPhotoPreview] = useState(null);
  const reviewPhotoInputRef = useRef();
  const [showReviewLoginPrompt, setShowReviewLoginPrompt] = useState(false);

  const [showCycleLogger, setShowCycleLogger] = useState(false);

  const handleMealClick = (meal) => {
    setSelectedMeal(meal);
    setShowRecipeModal(true);
  };

  const confirmDelete = (message) => {
    return window.confirm(message);
  };

  const handleShowDashboard = () => {
    setShowUserDashboard(true);
  };

  const handleShowMealPlans = () => {
    setShowUserMealPlans(true);
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      localStorage.removeItem('mealPlannerUser');
      clearToken(); 
      setCurrentUser(null);
      setUserEmail('');
      setMealPlan(null);
      setSavedMealPlans([]);
      setShowSavedPlans(false);
      alert('You have been logged out successfully!');
    }
  };

  const handleShowCycleLogger = () => {
    setShowCycleLogger(true);
  };

  const handleLogSaved = () => {
    // Refresh cycle data after logging
    fetchCycleData();
  };

  const handleUserUpdate = (updatedUser) => {
    setCurrentUser(updatedUser);
  };
  
  const handleShowCycleDashboard = () => {
  setShowCycleDashboard(true);
  };

  const handleCycleUpdate = (updatedCycleData) => {
    setCycleData(updatedCycleData);
  };

  const fetchCycleData = useCallback(async () => {
    if (!currentUser) return;

    try {
      const response = await authFetch(`${API_BASE_URL}/users/cycle/${currentUser.id}`);
      if (response.ok) {
        const data = await response.json();
        setCycleData(data);
      }
    } catch (error) {
      console.error('Error fetching cycle data:', error);
    }
  }, [currentUser]);

  const handleReviewPhotoSelect = (event) => {
  const file = event.target.files[0];
  if (file) {
    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert('Photo must be smaller than 5MB');
      return;
    }
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    
    setNewReview(prev => ({ ...prev, photo: file }));
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => setReviewPhotoPreview(e.target.result);
    reader.readAsDataURL(file);
  }
};

const removeReviewPhoto = () => {
  setNewReview(prev => ({ ...prev, photo: null }));
  setReviewPhotoPreview(null);
  if (reviewPhotoInputRef.current) {
    reviewPhotoInputRef.current.value = '';
  }
};


  // Enhanced mood options
  const moods = [
    { id: 'energetic', name: 'Energetic', color: 'bg-orange-500', emoji: '⚡', description: 'High-energy meals to fuel your day' },
    { id: 'comfort', name: 'Comfort', color: 'bg-amber-500', emoji: '🤗', description: 'Warm, cozy meals for relaxation' },
    { id: 'healthy', name: 'Healthy', color: 'bg-green-500', emoji: '🥗', description: 'Nutritious, balanced meals' },
    { id: 'indulgent', name: 'Indulgent', color: 'bg-purple-500', emoji: '🍰', description: 'Rich, satisfying treats' },
    { id: 'fresh', name: 'Fresh', color: 'bg-blue-500', emoji: '🌿', description: 'Light, refreshing meals' },
    { id: 'spicy', name: 'Spicy', color: 'bg-red-500', emoji: '🌶️', description: 'Bold, flavorful dishes with heat' }
  ];

  // Enhanced dietary preferences
  const dietaryOptions = [
    { value: 'Any', label: 'Any', icon: '🍽️' },
    { value: 'vegetarian', label: 'Vegetarian', icon: '🥬' },
    { value: 'vegan', label: 'Vegan', icon: '🌱' },
    { value: 'gluten-free', label: 'Gluten-Free', icon: '🌾' },
    { value: 'keto-friendly', label: 'Keto', icon: '🥑' },
    { value: 'high-protein', label: 'High Protein', icon: '💪' },
    { value: 'low-carb', label: 'Low Carb', icon: '🥩' },
    { value: 'dairy-free', label: 'Dairy-Free', icon: '🥛' },
    { value: 'paleo', label: 'Paleo', icon: '🦴' },
    { value: 'mediterranean', label: 'Mediterranean', icon: '🫒' }
  ];

  // Enhanced cuisine options
  const cuisineOptions = [
    { value: 'Any', label: 'Any Cuisine', icon: '🌍', color: 'bg-gray-100' },
    { value: 'Italian', label: 'Italian', icon: '🍝', color: 'bg-red-100' },
    { value: 'Asian', label: 'Asian', icon: '🥢', color: 'bg-yellow-100' },
    { value: 'Mexican', label: 'Mexican', icon: '🌮', color: 'bg-orange-100' },
    { value: 'Indian', label: 'Indian', icon: '🍛', color: 'bg-orange-200' },
    { value: 'American', label: 'American', icon: '🍔', color: 'bg-blue-100' },
    { value: 'Mediterranean', label: 'Mediterranean', icon: '🫒', color: 'bg-green-100' },
    { value: 'French', label: 'French', icon: '🥖', color: 'bg-purple-100' },
    { value: 'Thai', label: 'Thai', icon: '🍜', color: 'bg-pink-100' },
    { value: 'Japanese', label: 'Japanese', icon: '🍣', color: 'bg-indigo-100' },
    { value: 'Greek', label: 'Greek', icon: '🥙', color: 'bg-blue-200' },
    { value: 'Chinese', label: 'Chinese', icon: '🥟', color: 'bg-red-200' }
  ];

  // Popular ingredients list for suggestions
  const popularIngredients = [
    'Chicken', 'Beef', 'Fish', 'Eggs', 'Rice', 'Pasta', 'Potatoes', 'Onions',
    'Garlic', 'Tomatoes', 'Bell Peppers', 'Carrots', 'Broccoli', 'Spinach',
    'Cheese', 'Milk', 'Bread', 'Olive Oil', 'Lemon', 'Avocado', 'Mushrooms',
    'Ground Turkey', 'Salmon', 'Quinoa', 'Sweet Potatoes', 'Zucchini'
  ];

  const popularAllergies = ['Peanuts', 'Milk', 'Eggs', 'Fish', 'Tree Nuts', 
    'Shellfish', 'Soy', 'Wheat', 'Sesame'
  ];

  

  // Fetch functions

  const fetchSavedMealPlans = useCallback(async () => {
    if (!currentUser) return;

    try {
      const response = await authFetch(`${API_BASE_URL}/saved-meal-plans/${encodeURIComponent(currentUser.email)}`);
      if (response.ok) {
        const data = await response.json();
        setSavedMealPlans(data);
      } else {
        console.error('Failed to fetch saved meal plans');
      }
    } catch (error) {
      console.error('Error fetching saved meal plans:', error);
    }
  }, [currentUser]);

  // Load reviews on component mount
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await authFetch(`${API_BASE_URL}/reviews`);
        if (response.ok) {
          const data = await response.json();
          setReviews(data);
          console.log('Reviews loaded from database:', data.length);
        } else {
          console.error('Failed to fetch reviews:', response.status);
        }
      } catch (error) {
        console.error('Error fetching reviews:', error);
        setReviews((prev) => {
          if (prev.length > 0) {
            return prev;
          }

          return [
            {
              id: 1,
              name: "Sarah Johnson",
              content: "Meal Planner has transformed our family dinners. No more 'what's for dinner' stress!",
              rating: 5,
              created_at: new Date().toISOString()
            }
          ];
        });
      }
    };

    fetchReviews();
  }, []);

  useEffect(() => {
    fetchSavedMealPlans();
  }, [fetchSavedMealPlans]);

  useEffect(() => {
    fetchCycleData();
  }, [fetchCycleData]);

  useEffect(() => {
    const savedUser = localStorage.getItem('mealPlannerUser');
    if (savedUser) {
      setCurrentUser(JSON.parse(savedUser));
    }
  }, []);

  useEffect(() => {
    setNewReview((prev) => ({
      ...prev,
      name: currentUser?.name || ''
    }));
  }, [currentUser]);

  const checkUserExists = async (email) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/users/check/${encodeURIComponent(email)}`);
    if (response.ok) {
      const data = await response.json();
      console.log('User check result:', data);
      return data;
    } else {
      console.error('Failed to check user:', response.status);
      return { exists: false, in360: false };
    }
  } catch (error) {
    console.error('Error checking user:', error);
    return { exists: false, in360: false };
  }
};

const handleStartPlanning = async () => {
  // If user is already logged in, scroll to meal planner section
  if (currentUser) {
    document.getElementById('meal-planner')?.scrollIntoView({ behavior: 'smooth' });
    return;
  }

  // If no user is logged in, proceed with email check
  if (!userEmail.trim()) {
    setShowEmailPrompt(true);
    return;
  }

  const userCheck = await checkUserExists(userEmail);
  console.log('User check for planning:', userCheck);
  
  if (userCheck.exists) {
    // User exists - show login panel
    console.log('User exists, showing login');
    setShowRegistration({
      show: true,
      activePanel: 'login',
      userData360: null,
      email: userEmail
    });
  } else {
    // User doesn't exist - show signup
    console.log('User does not exist, showing signup');
    setShowRegistration({
      show: true,
      activePanel: userCheck.in360 ? 'username' : 'email',
      userData360: userCheck.in360 ? userCheck.data360 : null,
      email: userEmail
    });
  }
};

const handleEmailSubmit = async (e) => {
  e.preventDefault();
  if (!userEmail.trim()) return;

  console.log('Checking user email:', userEmail);
  const userCheck = await checkUserExists(userEmail);
  console.log('User check result:', userCheck);
  
  if (userCheck.exists) {
    // User exists - show login
    console.log('User exists, showing login panel');
    setShowEmailPrompt(false);
    setShowRegistration({
      show: true,
      activePanel: 'login',
      userData360: null,
      email: userEmail
    });
  } else {
    // User doesn't exist - show signup
    console.log('User does not exist, showing signup panel');
    setShowEmailPrompt(false);
    setShowRegistration({
      show: true,
      activePanel: userCheck.in360 ? 'username' : 'email',
      userData360: userCheck.in360 ? userCheck.data360 : null,
      email: userEmail
    });
  }
};

  const handleRegistrationComplete = (userData) => {
    setCurrentUser(userData.user);
    setShowRegistration(prev => ({ ...prev, show: false }));
    setTimeout(() => {
      chatRef.current?.openChat();
    }, 500);
  };

  const saveMealPlan = async (planName = null) => {
    if (!currentUser || !mealPlan) {
      alert('Please log in and generate a meal plan first');
      return;
    }
  
    setIsSavingPlan(true);
    
    try {
      const response = await authFetch(`${API_BASE_URL}/save-meal-plan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userEmail: currentUser.email,
          mealPlanName: planName || mealPlanName || `${selectedMood} Meal Plan`,
          moodContext: mealPlan.mood_context,
          breakfast: mealPlan.breakfast,
          lunch: mealPlan.lunch,
          dinner: mealPlan.dinner,
          snack: mealPlan.snack,
          totalCalories: mealPlan.total_calories
        }),
      });
  
      if (response.ok) {
        const data = await response.json();
        console.log('Meal plan saved successfully:', data);
        alert('Meal plan saved successfully!');
        setShowSaveModal(false);
        setMealPlanName('');
        if (showSavedPlans) {
          fetchSavedMealPlans();
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save meal plan');
      }
    } catch (error) {
      console.error('Error saving meal plan:', error);
      alert(`Error saving meal plan: ${error.message}`);
    } finally {
      setIsSavingPlan(false);
    }
  };
  
  const deleteSavedMealPlan = async (planId) => {
    if (!confirmDelete('Are you sure you want to delete this meal plan?')) return;
    
    try {
      const response = await authFetch(`${API_BASE_URL}/saved-meal-plans/${planId}`, {
        method: 'DELETE',
      });
  
      if (response.ok) {
        alert('Meal plan deleted successfully!');
        fetchSavedMealPlans();
      } else {
        throw new Error('Failed to delete meal plan');
      }
    } catch (error) {
      console.error('Error deleting meal plan:', error);
      alert(`Error deleting meal plan: ${error.message}`);
    }
  };

  const promptReviewLogin = (event) => {
    if (currentUser) return false;
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      if (event.target && event.target.blur) {
        event.target.blur();
      }
    }
    setShowReviewLoginPrompt(true);
    return true;
  };

  const submitReview = async (e) => {
  e.preventDefault();
  if (promptReviewLogin(e)) return;
  if (!newReview.name.trim() || !newReview.content.trim()) {
    alert('Please fill in both name and review content.');
    return;
  }

  setIsSubmittingReview(true);
  console.log('Submitting review with photo:', !!newReview.photo);

  try {
    // Create FormData for file upload
    const formData = new FormData();
    formData.append('name', newReview.name.trim());
    formData.append('content', newReview.content.trim());
    formData.append('rating', parseInt(newReview.rating));
    if (currentUser?.id) {
      formData.append('user_id', currentUser.id);
    }
    if (newReview.photo) {
      formData.append('photo', newReview.photo);
    }

    const response = await authFetch(`${API_BASE_URL}/reviews`, {
      method: 'POST',
      body: formData, // Don't set Content-Type header, let browser set it with boundary
    });

    console.log('Response status:', response.status);
    
    if (response.ok) {
      const data = await response.json();
      console.log('Review saved successfully:', data);
      setReviews([data, ...reviews]);
      setNewReview({ name: '', content: '', rating: 5, photo: null });
      setReviewPhotoPreview(null);
      if (reviewPhotoInputRef.current) {
        reviewPhotoInputRef.current.value = '';
      }
      alert('Thank you for your review! It has been saved successfully.');
    } else {
      const errorData = await response.json();
      console.error('Server error:', errorData);
      throw new Error(errorData.error || `Server error: ${response.status}`);
    }
  } catch (error) {
    console.error('Network/Request error:', error);
    alert(`Error saving review: ${error.message}`);
  } finally {
    setIsSubmittingReview(false);
  }
};

  // Add ingredient functions
  const addIngredient = () => {
    if (newIngredient.trim() && !availableIngredients.includes(newIngredient.trim())) {
      setAvailableIngredients([...availableIngredients, newIngredient.trim()]);
      setNewIngredient('');
    }
  };

  const removeIngredient = (ingredient) => {
    setAvailableIngredients(availableIngredients.filter(ing => ing !== ingredient));
  };

  const addPopularIngredient = (ingredient) => {
    if (!availableIngredients.includes(ingredient)) {
      setAvailableIngredients([...availableIngredients, ingredient]);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsDetectingIngredients(true);
    
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await authFetch(`${API_BASE_URL}/detect-ingredients`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        if (data.ingredients && data.ingredients.length > 0) {
          const newIngredients = data.ingredients.filter(ing => 
            !availableIngredients.includes(ing)
          );
          setAvailableIngredients([...availableIngredients, ...newIngredients]);
          alert(`Detected ${data.ingredients.length} ingredients!`);
        } else {
          alert('No food ingredients detected in the image. Try a clearer photo or add ingredients manually.');
        }
      } else {
        throw new Error('Failed to detect ingredients');
      }
    } catch (error) {
      console.error('Error detecting ingredients:', error);
      alert('Unable to detect ingredients. Please check the backend service or add ingredients manually.');
    } finally {
      setIsDetectingIngredients(false);
    }
  };

  const addAllergy = () => {
    if (newAllergy.trim() && !userAllergies.includes(newAllergy.trim())) {
      setUserAllergies([...userAllergies, newAllergy.trim()]);
      setNewAllergy('');
    }
  };

  const removeAllergy = (allergen) => {
    setUserAllergies(userAllergies.filter(ing => ing !== allergen));
  };

  const addPopularAllergy = (allergen) => {
    if (!userAllergies.includes(allergen)) {
      setUserAllergies([...userAllergies, allergen]);
    }
  };

  const generateMealPlan = async (isNewPlan = false) => {
    setIsLoading(true);
    try {

      let menstrualData = null;
    if (currentUser && currentUser.sex === 'Female' && currentUser.track_menstrual_cycle) {
      try {
        const cycleRes = await authFetch(`${API_BASE_URL}/cycle-info/${currentUser.id}`);
        if (cycleRes.ok) {
          menstrualData = await cycleRes.json();
        }
      } catch (err) {
        console.log('Could not fetch cycle data:', err);
      }
    }
      const response = await authFetch(`${API_BASE_URL}/generate-ai-meal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mood: selectedMood,
          dietary: dietaryPref === 'Any' ? 'any' : dietaryPref, 
          cuisine: cuisinePref === 'Any' ? 'any' : cuisinePref,
          available_ingredients: availableIngredients,
          allergies: userAllergies,
          menstrualData 
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setMealPlan(data);
      } else {
        throw new Error('Failed to generate AI-based meal plan');
      }
    } catch (error) {
      console.error('Error generating AI meal plan:', error);
      alert('Unable to generate meal plan. Please check backend service.');
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Mood-Based Recommendations",
      description: "Get meal suggestions that match your current mood and energy levels"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Family Meal Coordination",
      description: "Plan meals for the whole family with dietary preferences in mind"
    },
    {
      icon: <DollarSign className="w-6 h-6" />,
      title: "Budget-Friendly Planning",
      description: "Stay within budget with cost-optimized meal plans and shopping lists"
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Smart Meal Planning",
      description: "Weekly meal plans with leftover optimization and prep scheduling"
    }
  ];

  // Save Meal Plan Button Component
  const SaveMealPlanButton = () => (
    <div className="mt-4 text-center">
      <button
        onClick={() => setShowSaveModal(true)}
        disabled={!currentUser || !mealPlan || isSavingPlan}
        className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-3 rounded-full font-semibold hover:from-blue-600 hover:to-purple-600 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSavingPlan ? (
          <>
            <Clock className="w-5 h-5 mr-2 inline animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <Heart className="w-5 h-5 mr-2 inline" />
            Save Meal Plan
          </>
        )}
      </button>
      {!currentUser && (
        <p className="text-sm text-gray-500 mt-2">Please log in to save meal plans</p>
      )}
    </div>
  );

  // Save Modal Component
  const SaveMealPlanModal = () => (
    showSaveModal && (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-6 max-w-md mx-4 shadow-2xl">
          <div className="text-center mb-6">
            <Heart className="w-16 h-16 text-purple-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Save Your Meal Plan</h3>
            <p className="text-gray-600">Give your meal plan a memorable name</p>
          </div>
          
          <div className="space-y-4">
            <input
              type="text"
              placeholder="e.g., My Energetic Monday Plan"
              value={mealPlanName}
              onChange={(e) => setMealPlanName(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              autoFocus
            />
            
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowSaveModal(false);
                  setMealPlanName('');
                }}
                className="flex-1 px-4 py-3 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={() => saveMealPlan()}
                disabled={isSavingPlan}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl hover:from-blue-600 hover:to-purple-600 transition-all disabled:opacity-50"
              >
                {isSavingPlan ? 'Saving...' : 'Save Plan'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  );

  // Saved Plans Viewer Component
  const SavedMealPlansViewer = () => (
    currentUser && (
      <div className="mt-8">
        <div className="text-center mb-6">
          <button
            onClick={() => {
              setShowSavedPlans(!showSavedPlans);
              if (!showSavedPlans) {
                fetchSavedMealPlans();
              }
            }}
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-full font-semibold hover:from-purple-600 hover:to-pink-600 transition-all"
          >
            {showSavedPlans ? 'Hide' : 'View'} My Saved Plans ({savedMealPlans.length})
          </button>
        </div>

        {showSavedPlans && (
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Your Saved Meal Plans</h3>
            
            {savedMealPlans.length === 0 ? (
              <div className="text-center py-8">
                <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No saved meal plans yet</p>
                <p className="text-sm text-gray-400">Generate and save your first meal plan!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {savedMealPlans.map((plan) => (
                  <div key={plan.id} className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-xl border border-purple-100">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-semibold text-gray-800">{plan.meal_plan_name}</h4>
                        <p className="text-sm text-gray-600 capitalize">{plan.mood_context} mood • {plan.total_calories} total calories</p>
                        <p className="text-xs text-gray-400">Saved on {new Date(plan.date_created).toLocaleDateString()}</p>
                      </div>
                      <button
                        onClick={() => deleteSavedMealPlan(plan.id)}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Delete
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="font-medium">🌅 Breakfast:</span> {plan.breakfast_name} ({plan.breakfast_calories} cal)
                      </div>
                      <div>
                        <span className="font-medium">🥗 Lunch:</span> {plan.lunch_name} ({plan.lunch_calories} cal)
                      </div>
                      <div>
                        <span className="font-medium">🍽️ Dinner:</span> {plan.dinner_name} ({plan.dinner_calories} cal)
                      </div>
                      <div>
                        <span className="font-medium">🍎 Snack:</span> {plan.snack_name} ({plan.snack_calories} cal)
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    )
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-green-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-orange-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <img src={Logo360} alt="360 Logo" className="w-8 h-8 rounded-full object-cover" />
            </div>
            <nav className="hidden md:flex space-x-8">
              <button
                  onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                  className="hover:text-orange-500"
                >
                  Features
                </button>
              <button
                  onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-700 hover:text-orange-600 transition-colors"
                >
                  How It Works
                </button>
              <button
                  onClick={() => document.getElementById('testimonials')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-700 hover:text-orange-600 transition-colors"
                >
                  Reviews
                </button>
            </nav>
            <div className="flex items-center space-x-4">
              {currentUser && cycleData && (
                <CyclePhaseBadge 
                  cycleData={cycleData}
                  onClick={handleShowCycleDashboard}
                />
              )}

              {currentUser ? (
                <UserDropdown 
                  user={currentUser}
                  onShowDashboard={handleShowDashboard}
                  onShowMealPlans={handleShowMealPlans}
                  onShowCycleDashboard={handleShowCycleDashboard}
                  onShowCycleLogger={handleShowCycleLogger}
                  onLogout={handleLogout}
                />
              ) : (
                <button 
                  onClick={handleStartPlanning}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2 rounded-full hover:from-orange-600 hover:to-orange-700 transition-all transform hover:scale-105"
                >
                  Start Planning
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex justify-center mb-6">
              <Sparkles className="w-12 h-12 text-orange-500 animate-pulse" />
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              <span className="bg-gradient-to-r from-orange-600 via-red-500 to-green-600 bg-clip-text text-transparent">
                Meal Planner
              </span>
              <br />
              <span className="text-gray-800">Meals That Match Your Mood</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Discover personalized meal plans based on your mood, health needs, and available ingredients. 
              Make every meal perfect for how you feel.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleStartPlanning}
                className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all transform hover:scale-105"
              >
                Start Planning Meals
              </button>
              <button 
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-2 border-orange-500 text-orange-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-orange-50 transition-all"
              >
                Learn More
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Meal Planner Section */}
      <section id="meal-planner" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              <span className="bg-gradient-to-r from-orange-600 via-red-500 to-green-600 bg-clip-text text-transparent">
                Smart Meal Planning
              </span>
              <br />
              <span className="text-gray-800">Just for You</span>
            </h2>
            <p className="text-xl text-gray-600">
              Tell us your mood, preferences, and available ingredients for personalized recommendations
            </p>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-green-50 rounded-3xl p-8 border border-orange-100">
            
            {/* Mood Selector - Enhanced */}
            <div className="mb-10">
              <h3 className="text-2xl font-semibold text-gray-800 mb-6 text-center">How are you feeling today?</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {moods.map((mood) => (
                  <button
                    key={mood.id}
                    onClick={() => setSelectedMood(mood.id)}
                    className={`p-4 rounded-2xl transition-all transform hover:scale-105 border-2 ${
                      selectedMood === mood.id
                        ? `${mood.color} text-white shadow-lg border-white`
                        : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-3xl mb-2">{mood.emoji}</div>
                      <div className="font-semibold text-lg">{mood.name}</div>
                      <div className={`text-xs mt-1 ${selectedMood === mood.id ? 'text-white/80' : 'text-gray-500'}`}>
                        {mood.description}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Available Ingredients Section */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">Available Ingredients</h3>
              
              {/* Add ingredients manually */}
              <div className="bg-white rounded-2xl p-6 mb-4">
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    placeholder="Add an ingredient..."
                    value={newIngredient}
                    onChange={(e) => setNewIngredient(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addIngredient()}
                    className="flex-1 px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                  <button
                    onClick={addIngredient}
                    className="bg-orange-500 text-white px-4 py-2 rounded-xl hover:bg-orange-600 transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>

                {/* Image upload for ingredient detection */}
                <div className="mb-4">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isDetectingIngredients}
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-3 rounded-xl hover:from-blue-600 hover:to-purple-600 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isDetectingIngredients ? (
                      <>
                        <Clock className="w-5 h-5 animate-spin" />
                        Detecting Ingredients...
                      </>
                    ) : (
                      <>
                        <Camera className="w-5 h-5" />
                        📸 Detect Ingredients from Photo
                      </>
                    )}
                  </button>
                </div>

                {/* Popular ingredients quick add */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-600 mb-2">Quick Add Popular Ingredients:</h4>
                  <div className="flex flex-wrap gap-2">
                    {popularIngredients.slice(0, 8).map((ingredient) => (
                      <button
                        key={ingredient}
                        onClick={() => addPopularIngredient(ingredient)}
                        disabled={availableIngredients.includes(ingredient)}
                        className={`px-3 py-1 rounded-full text-sm transition-colors ${
                          availableIngredients.includes(ingredient)
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                        }`}
                      >
                        {ingredient}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Display selected ingredients */}
                {availableIngredients.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-600 mb-2">Your Ingredients ({availableIngredients.length}):</h4>
                    <div className="flex flex-wrap gap-2">
                      {availableIngredients.map((ingredient, index) => (
                        <span
                          key={index}
                          className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                        >
                          {ingredient}
                          <button
                            onClick={() => removeIngredient(ingredient)}
                            className="text-green-600 hover:text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Allergies Section */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">Allergies</h3>
              
              {/* Add allergies manually */}
              <div className="bg-white rounded-2xl p-6 mb-4">
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    placeholder="Add an allergy..."
                    value={newAllergy}
                    onChange={(e) => setNewAllergy(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addAllergy()}
                    className="flex-1 px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                  <button
                    onClick={addAllergy}
                    className="bg-orange-500 text-white px-4 py-2 rounded-xl hover:bg-orange-600 transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>

                {/* Popular allergies quick add */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-600 mb-2">Quick Add Popular Allergies:</h4>
                  <div className="flex flex-wrap gap-2">
                    {popularAllergies.slice(0, 8).map((allergen) => (
                      <button
                        key={allergen}
                        onClick={() => addPopularAllergy(allergen)}
                        disabled={userAllergies.includes(allergen)}
                        className={`px-3 py-1 rounded-full text-sm transition-colors ${
                          userAllergies.includes(allergen)
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                        }`}
                      >
                        {allergen}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Display selected allergies */}
                {userAllergies.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-600 mb-2">Your Allergies ({userAllergies.length}):</h4>
                    <div className="flex flex-wrap gap-2">
                      {userAllergies.map((allergen, index) => (
                        <span
                          key={index}
                          className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                        >
                          {allergen}
                          <button
                            onClick={() => removeAllergy(allergen)}
                            className="text-red-600 hover:text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Enhanced Dietary Preferences */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">Dietary Preferences</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {dietaryOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setDietaryPref(option.value)}
                    className={`p-3 rounded-xl transition-all transform hover:scale-105 border-2 ${
                      dietaryPref === option.value
                        ? 'bg-green-500 text-white border-green-600 shadow-lg'
                        : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-lg mb-1">{option.icon}</div>
                      <div className="text-sm font-medium">{option.label}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Enhanced Cuisine Preferences */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">Preferred Cuisine</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {cuisineOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setCuisinePref(option.value)}
                    className={`p-4 rounded-xl transition-all transform hover:scale-105 border-2 ${
                      cuisinePref === option.value
                        ? 'bg-blue-500 text-white border-blue-600 shadow-lg'
                        : `${option.color} text-gray-700 hover:shadow-md border-gray-200`
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-2xl mb-2">{option.icon}</div>
                      <div className="font-medium text-sm">{option.label}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Generate Button */}
            <div className="text-center mb-8">
              <button
                onClick={() => generateMealPlan(false)}
                disabled={isLoading}
                className="bg-gradient-to-r from-orange-500 to-green-500 text-white px-12 py-4 rounded-full text-xl font-semibold hover:from-orange-600 hover:to-green-600 transition-all transform hover:scale-105 shadow-lg disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <Clock className="w-6 h-6 mr-3 inline animate-spin" />
                    Generating Your Perfect Meals...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-6 h-6 mr-3 inline" />
                    Generate My Meal Plan
                  </>
                )}
              </button>
              {availableIngredients.length > 0 && (
                <p className="text-sm text-green-600 mt-2">
                  ✨ Using {availableIngredients.length} of your available ingredients
                </p>
              )}
            </div>

            {/* Meal Plan Results */}
            {mealPlan?.mood_context && (
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-orange-100">
                <h4 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                  Your <span className="text-orange-600 capitalize">{mealPlan.mood_context}</span> Meal Plan
                </h4>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* LEFT COLUMN */}
                  <div className="space-y-4">
                    {/* Breakfast */}
                    {mealPlan.breakfast && (
                      <div className="bg-orange-50 p-4 rounded-xl border border-orange-100">
                        <h5 className="font-semibold text-orange-800 mb-2">🌅 Breakfast</h5>
                        <p
                          className="text-gray-700 font-medium cursor-pointer underline hover:text-orange-600"
                          onClick={() => handleMealClick(mealPlan.breakfast)}
                        >
                          {mealPlan.breakfast.name}
                        </p>
                        <p className="text-sm text-gray-600">
                          {mealPlan.breakfast.calories} cal • {mealPlan.breakfast.prep_time} min
                        </p>
                      </div>
                    )}

                    {/* Lunch */}
                    {mealPlan.lunch && (
                      <div className="bg-green-50 p-4 rounded-xl border border-green-100">
                        <h5 className="font-semibold text-green-800 mb-2">🥗 Lunch</h5>
                        <p
                          className="text-gray-700 font-medium cursor-pointer underline hover:text-orange-600"
                          onClick={() => handleMealClick(mealPlan.lunch)}
                        >
                          {mealPlan.lunch.name}
                        </p>
                        <p className="text-sm text-gray-600">
                          {mealPlan.lunch.calories} cal • {mealPlan.lunch.prep_time} min
                        </p>
                      </div>
                    )}
                  </div>

                  {/* RIGHT COLUMN */}
                  <div className="space-y-4">
                    {/* Dinner */}
                    {mealPlan.dinner && (
                      <div className="bg-purple-50 p-4 rounded-xl border border-purple-100">
                        <h5 className="font-semibold text-purple-800 mb-2">🍽️ Dinner</h5>
                        <p
                          className="text-gray-700 font-medium cursor-pointer underline hover:text-orange-600"
                          onClick={() => handleMealClick(mealPlan.dinner)}
                        >
                          {mealPlan.dinner.name}
                        </p>
                        <p className="text-sm text-gray-600">
                          {mealPlan.dinner.calories} cal • {mealPlan.dinner.prep_time} min
                        </p>
                      </div>
                    )}

                    {/* Snack */}
                    {mealPlan.snack && (
                      <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                        <h5 className="font-semibold text-blue-800 mb-2">🍎 Snack</h5>
                        <p
                          className="text-gray-700 font-medium cursor-pointer underline hover:text-orange-600"
                          onClick={() => handleMealClick(mealPlan.snack)}
                        >
                          {mealPlan.snack.name}
                        </p>
                        <p className="text-sm text-gray-600">
                          {mealPlan.snack.calories} cal • {mealPlan.snack.prep_time} min
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Total Calories Footer */}
                {mealPlan?.total_calories && (
                  <div className="mt-6 text-center bg-gradient-to-r from-orange-100 to-green-100 p-4 rounded-xl">
                    <p className="text-lg font-semibold text-gray-800">
                      Total Daily Calories: {mealPlan.total_calories}
                    </p>
                  </div>
                )}

                {/* Ingredients Used Display */}
                {availableIngredients.length > 0 && (
                  <div className="mt-4 bg-green-50 p-4 rounded-xl">
                    <h5 className="font-semibold text-green-800 mb-2">✅ Using Your Available Ingredients</h5>
                    <div className="flex flex-wrap gap-2">
                      {availableIngredients.slice(0, 6).map((ingredient, index) => (
                        <span key={index} className="bg-green-200 text-green-800 px-2 py-1 rounded-full text-xs">
                          {ingredient}
                        </span>
                      ))}
                      {availableIngredients.length > 6 && (
                        <span className="text-green-600 text-xs">
                          +{availableIngredients.length - 6} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Recipe Modal */}
            {showRecipeModal && selectedMeal && (
              <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-xl shadow-lg max-w-md w-full relative max-h-[80vh] overflow-y-auto">
                  <button 
                    onClick={() => setShowRecipeModal(false)} 
                    className="absolute top-2 right-3 text-gray-400 hover:text-red-500 text-xl"
                  >
                    ×
                  </button>
                  <h3 className="text-2xl font-bold text-orange-600 mb-3">{selectedMeal.name}</h3>
                  <p className="text-sm text-gray-500 mb-2">Calories: {selectedMeal.calories} • Prep Time: {selectedMeal.prep_time} min</p>
                  <h4 className="text-lg font-semibold text-gray-800 mt-4">Ingredients</h4>
                  <ul className="list-disc list-inside text-gray-700">
                    {selectedMeal.ingredients?.map((ing, i) => <li key={i}>{ing}</li>)}
                  </ul>
                  <h4 className="text-lg font-semibold text-gray-800 mt-4">Instructions</h4>
                  <p className="text-gray-700">{selectedMeal.instructions}</p>
                </div>
              </div>
            )}

            {/* Cycle Logger Modal */}
            {showCycleLogger && cycleData && (
            <CycleLogger 
              user={currentUser}
              cycleData={cycleData}
              onClose={() => setShowCycleLogger(false)}
              onLogSaved={handleLogSaved}
            />
            )}

            {/* Save Meal Plan Button */}
            <SaveMealPlanButton />
                
            {/* Try a Different Meal Plan Button */}
            <div className="mt-6 text-center">
              <button
                onClick={() => generateMealPlan(true)}
                disabled={isLoading}
                className="bg-gradient-to-r from-green-500 to-orange-500 text-white px-8 py-3 rounded-full font-semibold hover:from-green-600 hover:to-orange-600 transition-all transform hover:scale-105 disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <Clock className="w-5 h-5 mr-2 inline animate-spin" />
                    Generating New Plan...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2 inline" />
                    Try a Different Meal Plan
                  </>
                )}
              </button>
              <p className="text-sm text-gray-600 mt-2">
                {mealPlan?.plan_id ? `Plan ID: ${mealPlan.plan_id} • ` : ''}
                Generated at {new Date().toLocaleTimeString()}
              </p>
            </div>

        {/* Saved Plans Viewer */}
          <SavedMealPlansViewer />
        </div>
      </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              Why Choose Our Meal Planner?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The first meal planning platform that understands your mood, health, and available ingredients
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all transform hover:scale-105 border border-orange-100">
                <div className="bg-gradient-to-r from-orange-500 to-green-500 w-12 h-12 rounded-full flex items-center justify-center text-white mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600">
              Get personalized meal plans in 3 simple steps
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-gradient-to-r from-orange-500 to-red-500 w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="text-xl font-semibold mb-2">Tell Us About You</h3>
              <p className="text-gray-600">Share your mood, dietary preferences, and available ingredients</p>
            </div>
            
            <div className="text-center">
              <div className="bg-gradient-to-r from-red-500 to-purple-500 w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="text-xl font-semibold mb-2">Get Custom Meal Plans</h3>
              <p className="text-gray-600">Receive personalized meal plans using your available ingredients</p>
            </div>
            
            <div className="text-center">
              <div className="bg-gradient-to-r from-purple-500 to-green-500 w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="text-xl font-semibold mb-2">Cook & Enjoy</h3>
              <p className="text-gray-600">Follow easy recipes with ingredients you already have</p>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="testimonials" className="py-20 bg-gradient-to-r from-orange-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              What Our Users Say
            </h2>
            <p className="text-xl text-gray-600">
              Share your experience and read what others think
            </p>
          </div>
          
          {/* Add Review Form */}
          <div className="max-w-2xl mx-auto mb-12">
  <div className="bg-white rounded-2xl p-6 shadow-lg border border-orange-100">
    <h3 className="text-xl font-semibold text-gray-800 mb-4">Leave a Review</h3>
    <form onSubmit={submitReview} className="space-y-4">
      <div>
        <input
          type="text"
          placeholder="Your Name"
          value={newReview.name}
          onFocus={promptReviewLogin}
          onChange={(e) => {
            if (promptReviewLogin(e)) return;
            setNewReview({ ...newReview, name: e.target.value });
          }}
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          required
          disabled={isSubmittingReview}
        />
      </div>
      <div>
        <textarea
          placeholder="Share your experience with our Meal Planner..."
          value={newReview.content}
          onFocus={promptReviewLogin}
          onChange={(e) => {
            if (promptReviewLogin(e)) return;
            setNewReview({ ...newReview, content: e.target.value });
          }}
          rows="4"
          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
          required
          disabled={isSubmittingReview}
        />
      </div>
      
      {/* Photo Upload Section */}
      <div className="border border-gray-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-gray-700">Add a Photo (Optional)</span>
          <span className="text-xs text-gray-500">Max 5MB</span>
        </div>
        
        {!reviewPhotoPreview ? (
          <div 
            onClick={(e) => {
              if (promptReviewLogin(e)) return;
              reviewPhotoInputRef.current?.click();
            }}
            className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-orange-400 cursor-pointer transition-colors"
          >
            <Camera className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600">Click to add a photo of your meal</p>
            <p className="text-xs text-gray-400 mt-1">JPG, PNG, GIF up to 5MB</p>
          </div>
        ) : (
          <div className="relative">
            <img 
              src={reviewPhotoPreview} 
              alt="Review preview" 
              className="w-full h-48 object-cover rounded-lg"
            />
            <button
              type="button"
              onClick={removeReviewPhoto}
              className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        
        <input
          ref={reviewPhotoInputRef}
          type="file"
          accept="image/*"
          onChange={handleReviewPhotoSelect}
          className="hidden"
          disabled={isSubmittingReview}
        />
      </div>
      
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="text-gray-700">Rating:</span>
          <div className="flex">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                type="button"
                key={star}
                onClick={(e) => {
                  if (promptReviewLogin(e)) return;
                  setNewReview({ ...newReview, rating: star });
                }}
                disabled={isSubmittingReview}
                className={`${
                  star <= newReview.rating ? 'text-yellow-400' : 'text-gray-300'
                } hover:text-yellow-400 transition-colors disabled:cursor-not-allowed`}
              >
                <Star className="w-6 h-6 fill-current" />
              </button>
            ))}
          </div>
        </div>
        <button
          type="submit"
          disabled={isSubmittingReview}
          className="bg-gradient-to-r from-orange-500 to-green-500 text-white px-6 py-2 rounded-full hover:from-orange-600 hover:to-green-600 transition-all flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmittingReview ? (
            <>
              <Clock className="w-4 h-4 animate-spin" />
              <span>Submitting...</span>
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              <span>Submit Review</span>
            </>
          )}
        </button>
      </div>
    </form>
  </div>
</div>
          
          {/* Display Reviews */}
          {reviews.length > 0 ? (
  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
    {reviews.map((review) => (
      <div key={review.id} className="bg-white rounded-2xl p-6 shadow-lg border border-orange-100">
        <div className="flex mb-4">
          {[...Array(review.rating)].map((_, i) => (
            <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
          ))}
        </div>
        
        {/* Review Photo */}
        {review.photo_url && (
          <div className="mb-4">
            <img 
              src={review.photo_url} 
              alt="User review" 
              className="w-full h-48 object-cover rounded-lg hover:scale-105 transition-transform cursor-pointer"
              onClick={() => window.open(review.photo_url, '_blank')}
            />
          </div>
        )}
        
        <p className="text-gray-700 mb-4 italic">"{review.content}"</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <User className="w-5 h-5 text-gray-400" />
            <p className="font-semibold text-gray-800">{review.name}</p>
          </div>
          <p className="text-xs text-gray-500">
            {new Date(review.created_at).toLocaleDateString()}
          </p>
        </div>
      </div>
    ))}
  </div>
) : (
  <div className="text-center py-12">
    <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
    <h3 className="text-xl font-semibold text-gray-600 mb-2">No reviews yet</h3>
    <p className="text-gray-500">Be the first to share your experience!</p>
  </div>
)}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-green-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-4">
            Ready to Transform Your Meals?
          </h2>
          <p className="text-xl text-orange-100 mb-8">
            Join thousands of users who've already discovered smart meal planning with available ingredients
          </p>
          <button 
            onClick={handleStartPlanning}
            className="bg-white text-orange-600 px-8 py-4 rounded-full text-lg font-semibold hover:bg-orange-50 transition-all transform hover:scale-105 shadow-lg"
          >
            Start Planning Now - It's Free!
            <ArrowRight className="w-5 h-5 ml-2 inline" />
          </button>
          <p className="text-orange-100 mt-4">100% Free Forever • Use Your Own Ingredients</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <ChefHat className="w-6 h-6 text-orange-500" />
                <span className="text-xl font-bold">Meal Planner</span>
              </div>
              <p className="text-gray-400">
                Personalized meal planning that adapts to your mood, health, and available ingredients.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <button
                    type="button"
                    className="hover:text-orange-500"
                    onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    Features
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="hover:text-orange-500"
                    onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    Pricing
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="hover:text-orange-500"
                    onClick={() => document.getElementById('recipes')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    Recipes
                  </button>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <button
                    type="button"
                    className="hover:text-orange-500"
                    onClick={() => document.getElementById('help-center')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    Help Center
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="hover:text-orange-500"
                    onClick={() => document.getElementById('contact-us')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    Contact Us
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="hover:text-orange-500"
                    onClick={() => document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    FAQ
                  </button>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <button
                    type="button"
                    className="hover:text-orange-500"
                    onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    About
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="hover:text-orange-500"
                    onClick={() => document.getElementById('blog')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    Blog
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="hover:text-orange-500"
                    onClick={() => document.getElementById('privacy')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    Privacy
                  </button>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 Meal Planner. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Email Prompt Modal */}
      {showEmailPrompt && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl">
            <div className="text-center mb-6">
              <User className="w-16 h-16 text-orange-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Welcome Back!</h3>
              <p className="text-gray-600">Enter your email to access your meal plans</p>
            </div>
            
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <input
                type="email"
                placeholder="Enter your email address"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                required
                autoFocus
              />
              
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEmailPrompt(false);
                    setUserEmail('');
                  }}
                  className="flex-1 px-4 py-3 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-orange-500 to-green-500 text-white rounded-xl hover:from-orange-600 hover:to-green-600 transition-all"
                >
                  Continue
                </button>
              </div>
            </form>
            
            <p className="text-xs text-gray-500 text-center mt-4">
              New user? We'll guide you through a quick setup process.
            </p>
          </div>
        </div>
      )}

      {/* Review Login Prompt Modal */}
      {showReviewLoginPrompt && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl relative">
            <button
              onClick={() => setShowReviewLoginPrompt(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-orange-500" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Login Required</h3>
              <p className="text-gray-600">Please log in to write a review.</p>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowReviewLoginPrompt(false);
                  setShowRegistration({ show: true, activePanel: 'login', userData360: null, email: '' });
                }}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all font-semibold"
              >
                Log In
              </button>
              <button
                onClick={() => {
                  setShowReviewLoginPrompt(false);
                  setShowRegistration({ show: true, activePanel: 'email', userData360: null, email: '' });
                }}
                className="w-full border-2 border-orange-500 text-orange-600 px-6 py-3 rounded-xl hover:bg-orange-50 transition-all font-semibold"
              >
                Create Account
              </button>
              <button
                onClick={() => setShowReviewLoginPrompt(false)}
                className="w-full text-gray-500 hover:text-gray-700 px-6 py-2 rounded-xl transition-all font-medium"
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Registration Modal */}
      <UserRegistration 
        isOpen={showRegistration.show}
        onClose={() => {
          setShowRegistration({ show: false, activePanel: 'email', userData360: null, email: '' })}}
        onComplete={handleRegistrationComplete}
        activePanel={showRegistration.activePanel}
        username360={showRegistration.userData360?.username}
        name360={showRegistration.userData360?.name}
        age360={showRegistration.userData360?.age}
        initialEmail={showRegistration.email}
      />

      {/* Save Modal */}
      <SaveMealPlanModal />

      {/* User Dashboard Modal */}
      {showUserDashboard && (
      <UserDashboard 
      user={currentUser}
      onClose={() => setShowUserDashboard(false)}
      onUserUpdate={handleUserUpdate}
      />
      )}

      {/* User Meal Plans Modal */}
      {showUserMealPlans && (
      <UserMealPlans 
        user={currentUser}
        onClose={() => setShowUserMealPlans(false)}
      />
      )}

      {/* Cycle Dashboard Modal */}
{showCycleDashboard && (
  <CycleDashboard 
    user={currentUser}
    cycleData={cycleData}
    onClose={() => setShowCycleDashboard(false)}
    onUpdateCycle={handleCycleUpdate}
  />
)}


      {/* Chatbot */}
      <Chatbot ref={chatRef} user={currentUser} />
    </div>
    );
  }
  
  export default App;